import axios from 'axios';

import type {
  EnableSlaRuleRequest,
  SlaRuleCreatedResponse,
  SlaRuleCreateRequest,
  SlaRulePreviewRequest,
  SlaRulePreviewResult,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

/**
 * Mutating calls for the SLA Engine config screen. Reads go through `useDataFetcher`; these are
 * the writes. Each wraps the error in `reportAxiosError` (an error dialog) and rethrows so the caller can
 * reset its processing flag.
 *
 * The `/sysconfig/api/v1` prefix — not the `/api/v1` the Guardrails sibling uses — is what the
 * service registry mounts a module's `get_sysconfig_blueprint` under. Verified against the
 * generated `openapi.json`, which is emitted from the registered app.
 */
const slaRulesBaseUrl = (organizationId: number): string =>
  `/sysconfig/api/v1/organizations/${organizationId}/sla_rules`;

export const slaRulesUrl = (organizationId: number): string => `${slaRulesBaseUrl(organizationId)}/rules`;

export const slaTemplatesUrl = (organizationId: number): string => `${slaRulesBaseUrl(organizationId)}/templates`;

export const slaTemplateMetadataUrl = (organizationId: number, cardId: number): string =>
  `${slaTemplatesUrl(organizationId)}/${cardId}/metadata`;

/** Addressed by the parameter's `id`, not its `slug`: the slug is only the `context_params` key. */
export const slaTemplateParamValuesUrl = (organizationId: number, cardId: number, paramId: string): string =>
  `${slaTemplatesUrl(organizationId)}/${cardId}/params/${encodeURIComponent(paramId)}/values`;

export const slaRecipientOptionsUrl = (organizationId: number): string =>
  `${slaRulesBaseUrl(organizationId)}/recipient_options`;

/** One route for both directions, the body carrying which — unlike the Fund Accounts sibling's two
 *  bodiless `…/enable` and `…/disable` routes. The request model forbids extra fields, so the body
 *  is exactly `EnableSlaRuleRequest`. */
export const setSlaRuleEnabled = async (organizationId: number, ruleId: number, isEnabled: boolean): Promise<void> => {
  try {
    await axios.post(`${slaRulesUrl(organizationId)}/${ruleId}/enable`, {
      is_enabled: isEnabled,
    } satisfies EnableSlaRuleRequest);
  } catch (error) {
    await reportAxiosError(error);
    throw error;
  }
};

export const deleteSlaRule = async (organizationId: number, ruleId: number): Promise<void> => {
  try {
    await axios.delete(`${slaRulesUrl(organizationId)}/${ruleId}`);
  } catch (error) {
    await reportAxiosError(error);
    throw error;
  }
};

export const createSlaRule = async (organizationId: number, body: SlaRuleCreateRequest): Promise<number> => {
  try {
    const { data } = await axios.post<SlaRuleCreatedResponse>(slaRulesUrl(organizationId), body);
    return data.id;
  } catch (error) {
    await reportAxiosError(error);
    throw error;
  }
};

// Typed as the create body: the update body is the same field set with its own enum copy, so one write shape serves both.
export const updateSlaRule = async (
  organizationId: number,
  ruleId: number,
  body: SlaRuleCreateRequest
): Promise<void> => {
  try {
    await axios.put(`${slaRulesUrl(organizationId)}/${ruleId}`, body);
  } catch (error) {
    await reportAxiosError(error);
    throw error;
  }
};

/** A POST that writes nothing: the unsaved rule has to travel in a body. A 503 (no data snapshot yet)
 *  is reported like any other error; the dialog stays open and Save is unaffected. */
export const previewSlaRule = async (
  organizationId: number,
  body: SlaRulePreviewRequest
): Promise<SlaRulePreviewResult> => {
  try {
    const { data } = await axios.post<SlaRulePreviewResult>(`${slaRulesUrl(organizationId)}/preview`, body);
    return data;
  } catch (error) {
    await reportAxiosError(error);
    throw error;
  }
};
